import {create} from 'zustand';
import {authApi} from '../services/api/authApi';
import {secureStorage} from '../services/storage/secureStorage';

export const useAuthStore = create((set, get) => ({
  user: null,
  accessToken: null,
  refreshToken: null,
  isAuthenticated: false,
  isLoading: true,
  hasStore: null,

  loginWithOtp: async (countryCode, phone, otp) => {
    const response = await authApi.verifyOtp({countryCode, phone, otp, purpose: 'LOGIN'});
    const {accessToken, refreshToken, user} = response.data.data;
    await secureStorage.saveTokens(accessToken, refreshToken);
    set({user, accessToken, refreshToken, isAuthenticated: true, hasStore: user.hasStore});
  },

  register: async data => {
    const response = await authApi.register(data);
    const {accessToken, refreshToken, user} = response.data.data;
    await secureStorage.saveTokens(accessToken, refreshToken);
    set({user, accessToken, refreshToken, isAuthenticated: true, hasStore: false});
  },

  logout: async () => {
    try {
      const {refreshToken} = get();
      if (refreshToken) {
        await authApi.logout({refreshToken});
      }
    } catch (_) {
      // Ignore logout API errors
    }
    await secureStorage.clearTokens();
    set({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      hasStore: null,
    });
  },

  refreshTokens: async () => {
    const {refreshToken} = get();
    if (!refreshToken) throw new Error('No refresh token');

    const response = await authApi.refreshToken({refreshToken});
    const {accessToken: newAccess, refreshToken: newRefresh} =
      response.data.data;
    await secureStorage.saveTokens(newAccess, newRefresh);
    set({accessToken: newAccess, refreshToken: newRefresh});
  },

  loadStoredAuth: async () => {
    try {
      const tokens = await secureStorage.getTokens();
      if (tokens?.accessToken) {
        set({
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          isAuthenticated: true,
        });
        // Load user profile
        const response = await authApi.getMe();
        const user = response.data.data;
        set({user, hasStore: user.hasStore});
      }
    } catch (_) {
      await secureStorage.clearTokens();
      set({
        user: null,
        accessToken: null,
        refreshToken: null,
        isAuthenticated: false,
        hasStore: null,
      });
    } finally {
      set({isLoading: false});
    }
  },

  setHasStore: value => set({hasStore: value}),

  hasPermission: permission => {
    const {user} = get();
    if (!user) return false;
    if (user.owner || user.isOwner) return true;
    return user.permissions?.includes(permission) || false;
  },
}));
